/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ReservationDriver } from 'types/entities/ReservationDriver'
import { ReservationSlot } from 'types/entities/ReservationSlot'

type ReservationResultScalar = string | number | boolean | null
type ReservationResultObject = Record<string, ReservationResultScalar | ReservationResultScalar[]>
type ReservationResultValue =
    | ReservationResultScalar
    | ReservationResultScalar[]
    | ReservationResultObject

export interface ReservationState {
    drivers: ReservationDriver[]
    slots: ReservationSlot[]
    result: ReservationResultValue | null
}

const initialState: ReservationState = {
    drivers: [],
    slots: [],
    result: null,
}

const reservationSlice = createSlice({
    name: 'reservation',
    initialState,
    reducers: {
        createReservation(state) {
            return initialState
        },
        deleteReservationDriver(state, action: PayloadAction<{ driver: ReservationDriver }>) {
            state.drivers = state.drivers.filter(
                (driver) => driver.identifier !== action.payload.driver.identifier
            )
        },
        setReservationSlots(state, action: PayloadAction<{ slots: ReservationSlot[] }>) {
            state.slots = action.payload.slots
        },
        createReservationDriver(
            state,
            action: PayloadAction<{ driver: ReservationDriver; oldDriver?: ReservationDriver }>
        ) {
            const { driver: newDriver, oldDriver } = action.payload
            if (oldDriver) {
                const index = state.drivers.findIndex(
                    (d) => d.identifier === oldDriver.identifier
                )
                if (index !== -1) {
                    state.drivers[index] = newDriver
                }
            } else {
                state.drivers.push(newDriver)
            }
        },
        setReservationResult(
            state,
            action: PayloadAction<{ result: ReservationResultValue }>
        ) {
            state.result = action.payload.result
        },
        resetState: () => initialState,
    },
})

export const {
    createReservation,
    deleteReservationDriver,
    setReservationSlots,
    createReservationDriver,
    setReservationResult,
    resetState,
} = reservationSlice.actions

export default reservationSlice.reducer
